import { blupayApi } from "../api/client";

export const adminService = {
  /** Effective permissions for the signed-in staff member. */
  async getPermissions() {
    const { data } = await blupayApi.get("/auth/permissions");
    return data;
  },

  /**
   * Work currently waiting on an operator, counted per category.
   *
   * Derived on read by the backend - there is no scheduler, so this is the
   * queue at the moment of the request, not an alert stream.
   */
  async getAttention() {
    const { data } = await blupayApi.get("/admin/attention");
    return data;
  },

  async getOverview() {
    const { data } = await blupayApi.get("/admin/overview");
    return data;
  },

  /** Returns a page envelope: { items, total, limit, offset }. */
  async listUsers({
    limit = 25,
    offset = 0,
    search,
    kycStatus,
    accountStatus,
  } = {}) {
    const { data } = await blupayApi.get("/admin/users", {
      params: {
        limit,
        offset,
        search: search || undefined,
        kyc_status: kycStatus || undefined,
        account_status: accountStatus || undefined,
      },
    });
    return data;
  },

  async updateUser(userId, payload) {
    const { data } = await blupayApi.patch(`/admin/users/${userId}`, payload);
    return data;
  },

  /** Suspend, close, or reactivate an account. Reason required unless activating. */
  async updateAccountStatus(userId, { status, reason }) {
    const { data } = await blupayApi.patch(
      `/admin/users/${userId}/account-status`,
      { status, reason: reason || undefined }
    );
    return data;
  },

  /**
   * Grantable roles and the permissions each confers, read from the backend
   * permission registry. Kept server-side so a role added to
   * `ROLE_PERMISSIONS` becomes grantable with no frontend change.
   */
  async listRoles() {
    const { data } = await blupayApi.get("/admin/roles");
    return data;
  },

  async updateUserRole(userId, { role, reason }) {
    const { data } = await blupayApi.patch(`/admin/users/${userId}/role`, {
      role,
      reason,
    });
    return data;
  },

  /** Returns a page envelope: { items, total, limit, offset }. */
  async listTransfers({
    limit = 25,
    offset = 0,
    status,
    search,
    createdFrom,
    createdTo,
  } = {}) {
    const { data } = await blupayApi.get("/admin/transfers", {
      params: {
        limit,
        offset,
        status: status && status !== "all" ? status : undefined,
        search: search || undefined,
        created_from: createdFrom || undefined,
        created_to: createdTo || undefined,
      },
    });
    return data;
  },

  /**
   * Returns `{ items, total, limit, offset, total_open, total_high_risk,
   * total_blocked }`. The counters cover the whole queue, not the page.
   */
  async listComplianceCases({ limit = 25, offset = 0, search, riskLevel } = {}) {
    const { data } = await blupayApi.get("/admin/compliance", {
      params: {
        limit,
        offset,
        search: search || undefined,
        risk_level: riskLevel && riskLevel !== "all" ? riskLevel : undefined,
      },
    });
    return data;
  },

  async updateComplianceStatus(userId, { status, reason }) {
    const { data } = await blupayApi.patch(
      `/admin/users/${userId}/compliance`,
      { status, reason: reason || undefined }
    );
    return data;
  },

  /** Sanctions candidates requiring or carrying a human disposition. */
  async listSanctionsMatches({ limit = 25, offset = 0, disposition = "pending" } = {}) {
    const { data } = await blupayApi.get("/admin/sanctions/matches", {
      params: {
        limit,
        offset,
        disposition: disposition || undefined,
      },
    });
    return data;
  },

  async getSanctionsMatch(matchId) {
    const { data } = await blupayApi.get(`/admin/sanctions/matches/${matchId}`);
    return data;
  },

  async reviewSanctionsMatch(matchId, { disposition, reason }) {
    const { data } = await blupayApi.patch(`/admin/sanctions/matches/${matchId}`, {
      disposition,
      reason,
    });
    return data;
  },

  /** Internal AML decisions. Staff-only; never surfaced to customers. */
  async listAmlRiskDecisions({ senderUserId, decision, limit = 50 } = {}) {
    const { data } = await blupayApi.get("/admin/aml-risk-decisions", {
      params: {
        sender_user_id: senderUserId || undefined,
        decision: decision || undefined,
        limit,
      },
    });
    return data;
  },

  /**
   * The signed-in staff member's own audit entries.
   *
   * Separate from listAuditLogs because it needs no `audit:read`: reading your
   * own actions is self-service, not oversight. The backend forces the actor
   * filter to the caller.
   */
  async listMyAuditLogs({ limit = 20, offset = 0 } = {}) {
    const { data } = await blupayApi.get("/admin/me/audit-logs", {
      params: { limit, offset },
    });
    return data;
  },

  /** Append-only trail of privileged admin actions. */
  async listAuditLogs({
    limit = 25,
    offset = 0,
    action,
    targetType,
    targetId,
    actorUserId,
  } = {}) {
    const { data } = await blupayApi.get("/admin/audit-logs", {
      params: {
        limit,
        offset,
        action: action || undefined,
        target_type: targetType || undefined,
        target_id: targetId || undefined,
        actor_user_id: actorUserId || undefined,
      },
    });
    return data;
  },

  /**
   * Every corridor BluPay offers, the routes that carry it (preferred first)
   * and, for the pairs that cannot, why. Computed from each provider's code,
   * so it is read-only: a route changes by pull request.
   */
  async listRoutingCorridors() {
    const { data } = await blupayApi.get("/admin/routing/corridors");
    return data;
  },

  /** LLM keys in fallback order. Responses carry `key_hint`, never the key. */
  async listLlmKeys() {
    const { data } = await blupayApi.get("/admin/llm-keys");
    return data;
  },

  async createLlmKey({ displayName, provider, model, baseUrl, apiKey, isEnabled = true }) {
    const { data } = await blupayApi.post("/admin/llm-keys", {
      display_name: displayName,
      provider,
      model,
      base_url: baseUrl || null,
      api_key: apiKey,
      is_enabled: isEnabled,
    });
    return data;
  },

  /** Only the fields given are sent; `apiKey` rotates the key; disabling needs `reason`. */
  async updateLlmKey(keyId, { displayName, model, baseUrl, apiKey, isEnabled, reason }) {
    const payload = {};
    if (displayName !== undefined) payload.display_name = displayName;
    if (model !== undefined) payload.model = model;
    if (baseUrl) payload.base_url = baseUrl;
    if (apiKey) payload.api_key = apiKey;
    if (isEnabled !== undefined) payload.is_enabled = isEnabled;
    if (reason) payload.reason = reason;
    const { data } = await blupayApi.patch(`/admin/llm-keys/${keyId}`, payload);
    return data;
  },

  /** `keyIds` must list every key exactly once, first = tried first. */
  async reorderLlmKeys(keyIds) {
    const { data } = await blupayApi.put("/admin/llm-keys/order", { key_ids: keyIds });
    return data;
  },

  async deleteLlmKey(keyId, reason) {
    await blupayApi.delete(`/admin/llm-keys/${keyId}`, { params: { reason } });
  },

  /** Tests a saved key when `keyId` is given, otherwise the unsaved form values. */
  async testLlmKey({ keyId, provider, model, baseUrl, apiKey }) {
    if (keyId) {
      const { data } = await blupayApi.post(`/admin/llm-keys/${keyId}/test`);
      return data;
    }
    const { data } = await blupayApi.post("/admin/llm-keys/test", {
      provider,
      model,
      base_url: baseUrl || null,
      api_key: apiKey,
    });
    return data;
  },

  async getConfiguration() {
    const { data } = await blupayApi.get("/health/configuration");
    return data;
  },
};
